import { useState } from "react";
import { LuBot, LuGlobe, LuPlay, LuUser } from "react-icons/lu";
import { mayAutoplay, TileVideo } from "./GameVideo";
import { Link } from "../router";
import { GameArt } from "./GameArt";
import { GAME_META } from "./ui";

export interface GameRule { min: number; max: number; solo: boolean; bot: boolean; online: boolean }

/** the games sorted into three drawers, so that sixteen tiles never feel like a wall */
export const GAME_GROUPS: { id: string; title: string; sub: string; games: string[] }[] = [
  { id: "quiz", title: "Hurtige quiz", sub: "Svar hurtigt!", games: ["quizduel", "speedrun", "videnraes"] },
  { id: "bord", title: "Brætspil og kort", sub: "Svar for at rykke", games: ["monopoly", "skak", "fire", "kryds", "slagskibe", "kortkamp", "ludo", "yatzy"] },
  { id: "puslespil", title: "Puslespil og hjernegymnastik", sub: "Tænk dig frem – alene", games: ["sudoku", "videnspar"] },
  { id: "eventyr", title: "Eventyr", sub: "Forsvar og find skatten", games: ["verden", "taarn", "skattejagt"] },
];

/** a ribbon for the games that are played in real 3D */
const THREE_D = new Set(["taarn", "monopoly"]);
/** the five games that came with v16 get a "Nyt" ribbon */
export const NEW_GAMES = new Set(["verden"]);

/**
 * One game tile. At rest the picture is the game's own illustration (see GameArt); the short film plays only while the mouse (or keyboard focus)
 * is on the tile, and the picture comes back when it leaves. Below the picture: the name, three little icons for how it can be played, and "Spil".
 */
export function GameTile({ game, rule, size = "m" }: { game: string; rule?: GameRule; size?: "m" | "l" }) {
  const m = GAME_META[game];
  if (!m) return null;
  /* never on touch screens, and never with reduced motion / data saver */
  const [peek, setPeek] = useState(false);
  const enter = (e: { pointerType?: string }) => { if (e.pointerType === "mouse" && mayAutoplay()) setPeek(true); };
  const leave = (e: { pointerType?: string }) => { if (e.pointerType === "mouse") setPeek(false); };
  return (
    <Link to={game === "verden" ? "/verden" : `/spil/${game}`} className={`gtile ${size}${peek ? " peek" : ""}`} style={{ ["--c1" as any]: m.c1, ["--c2" as any]: m.c2 }} data-tilt="4" onPointerEnter={enter} onPointerLeave={leave} onFocus={() => mayAutoplay() && setPeek(true)} onBlur={() => setPeek(false)}>
      <span className="gt-art" aria-hidden="true">
        <GameArt game={game} className="gt-pic" />
        {THREE_D.has(game) && <span className="gt-ribbon">3D</span>}
        {NEW_GAMES.has(game) && <span className="gt-ribbon new">Nyt!</span>}
        <TileVideo game={game} on={peek} />
        <span className="gt-play"><LuPlay size={15} /></span>
      </span>
      <span className="gt-body">
        <strong>{m.name}</strong>
        <span className="gt-tag">{m.tagline}</span>
        <span className="gt-foot">
          <span className="gt-pills">
            {rule?.solo && <span title="Alene"><LuUser aria-hidden="true" size={15} /><i className="sr-only">Alene</i></span>}
            {rule?.bot && <span title="Mod computeren"><LuBot aria-hidden="true" size={15} /><i className="sr-only">Computer</i></span>}
            {rule?.online && <span title="Online"><LuGlobe aria-hidden="true" size={15} /><i className="sr-only">Online</i></span>}
          </span>
          <span className="gt-go"><LuPlay aria-hidden="true" size={14} /> Spil</span>
        </span>
      </span>
    </Link>
  );
}
